import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  GridComponent, ColumnsDirective, ColumnDirective,
  Inject, Page, Sort,
} from '@syncfusion/ej2-react-grids';
import type { RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { Box, Button, Snackbar, Alert, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { DepartmentRuleView, StaffingRuleView } from '../../../../services/LaborManagementService/types/laborManagementTypes';
import DepartmentRuleDialog from './DepartmentRuleDialog';

// A staleness refusal arrives as an RTK Query rejection carrying a 409, and the page answers it
// with its own warning. Read here rather than imported from `useScopedRuleSave`, because that
// module pulls in the service layer and this component's test renders without one.
const isStaleSaveRefusal = (error: unknown) => (error as { status?: unknown } | undefined)?.status === 409;

// The fields the rule dialog edits. The dialog shows a missing note as an empty box, so the note is
// read the same way here, or a rule stored without one would never compare unchanged.
const EDITED_FIELDS = ['formulaType', 'scalingFactor', 'utilizationFactor', 'enabled'] as const;
const isUnchanged = (stored: DepartmentRuleView, saved: DepartmentRuleView) =>
  EDITED_FIELDS.every((field) => stored[field] === saved[field]) && (stored.notes || '') === saved.notes;

// ── Display value mappings ─────────────────────────────────────────

const FORMULA_TYPE_MAP: Record<string, string> = {
  CaseBased: 'Case Based',
  CaseDurationBased: 'Case Duration Based',
  RoomBased: 'Room Based',
  TrayBased: 'Tray Based',
  Manual: 'Manual',
};

const DEPARTMENT_NAME_MAP: Record<string, string> = {
  PreOpRN: 'Pre-Op RN',
  PacuRN: 'PACU RN',
  ORCirculatingRN: 'OR Circulating RN',
  ORScrubTech: 'OR Scrub Tech',
  XrayTech: 'X-ray Tech',
  FirstAssist: 'First Assist',
  SterileProcessingTech: 'Sterile Processing Tech',
  PatientCareTech: 'Patient Care Tech',
  ORRunnerFloat: 'OR Runner/Float',
};

// ── Grid styles (matches codebase pattern) ─────────────────────────

const gridContainerStyles: SxProps<Theme> = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f8fafc',
    cursor: 'pointer',
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

// ── Columns ────────────────────────────────────────────────────────

const departmentValueAccessor = (_field: string, data: Object) => {
  const row = data as DepartmentRuleView;
  return DEPARTMENT_NAME_MAP[row.departmentName] ?? row.departmentName;
};

const formulaTypeValueAccessor = (_field: string, data: Object) => {
  const row = data as DepartmentRuleView;
  return FORMULA_TYPE_MAP[row.formulaType] ?? row.formulaType;
};

// The column set never varies by row or by prop, so it is built once here rather than rebuilt on
// every render, next to the display maps its accessors read.
const ruleColumns = (
  <ColumnsDirective>
    <ColumnDirective field="id" isPrimaryKey={true} visible={false} />
    <ColumnDirective
      field="departmentName"
      headerText="Department"
      width="180"
      textAlign="Left"
      valueAccessor={departmentValueAccessor}
    />
    <ColumnDirective
      field="formulaType"
      headerText="Formula Type"
      width="150"
      textAlign="Left"
      valueAccessor={formulaTypeValueAccessor}
    />
    <ColumnDirective field="scalingFactor" headerText="Scaling Factor" width="130" textAlign="Right" format="N2" />
    <ColumnDirective field="utilizationFactor" headerText="Utilization" width="110" textAlign="Right" format="N2" />
    <ColumnDirective field="enabled" headerText="Enabled" width="100" textAlign="Center" displayAsCheckBox={true} />
    <ColumnDirective field="notes" headerText="Notes" width="200" textAlign="Left" />
  </ColumnsDirective>
);

// ── Add Rule button ────────────────────────────────────────────────

interface AddRuleButtonProps {
  /** The departments that already carry a rule. A full center has nothing left to add. */
  existingDepartments: string[];
  isSaving: boolean;
  onClick: () => void;
}

// Its own component because the grid is at the complexity threshold, and because the button owns
// one self-contained rule: with a rule on every department the add dialog opens with an empty
// department list, and the delete control is gone since spec 127, so a full center stays full.
// The count is read off the display map rather than written as a second literal nine, because that
// map already mirrors the department enum.
const AddRuleButton: React.FC<AddRuleButtonProps> = ({ existingDepartments, isSaving, onClick }) => {
  const everyDepartmentHasARule = existingDepartments.length >= Object.keys(DEPARTMENT_NAME_MAP).length;
  return (
    // An empty title keeps the tooltip shut while the button is live. The span is what a disabled
    // button needs to stay focusable, or the tooltip never appears at all.
    <Tooltip title={everyDepartmentHasARule ? 'Every department already has a rule' : ''}>
      <span>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onClick}
          disabled={isSaving || everyDepartmentHasARule}
          size="small"
          sx={{
            backgroundColor: '#3b82f6',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#2563eb' },
          }}
        >
          Add Rule
        </Button>
      </span>
    </Tooltip>
  );
};

// ── The grid and its selection ─────────────────────────────────────

interface RulesGridProps {
  rules: DepartmentRuleView[];
  /** Whether the rule dialog is open. The grid's selection mirrors it. */
  dialogOpen: boolean;
  onRowSelected: (args: RowSelectEventArgs) => void;
}

// The widget raises no event for a click on the row that is already selected, so a row the dialog
// left selected swallows the first click after Cancel. The selection therefore belongs to the
// grid and is cleared whenever no dialog is open. Its own component because the ref and that rule
// are the grid's alone, and because the editor is at the complexity threshold without them.
const RulesGrid: React.FC<RulesGridProps> = ({ rules, dialogOpen, onRowSelected }) => {
  const gridRef = useRef<GridComponent>(null);

  useEffect(() => {
    if (!dialogOpen) gridRef.current?.clearRowSelection();
  }, [dialogOpen]);

  return (
    <Box sx={gridContainerStyles}>
      <GridComponent
        ref={gridRef}
        dataSource={rules}
        allowPaging={false}
        allowSorting={true}
        rowSelected={onRowSelected}
        height="auto"
      >
        {ruleColumns}
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
};

// ── Component ──────────────────────────────────────────────────────

interface RulesEditorGridProps {
  staffingRule: StaffingRuleView;
  /** Sends the one rule that was edited or added. Untouched rules never leave the browser. */
  onSaveRule: (rule: DepartmentRuleView) => Promise<void>;
  isSaving: boolean;
  readOnly?: boolean;
}

const RulesEditorGrid: React.FC<RulesEditorGridProps> = ({ staffingRule, onSaveRule, isSaving, readOnly = false }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<DepartmentRuleView | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // List of departments already in use (for validating add)
  const existingDepartments = useMemo(
    () => staffingRule.rules.map(r => r.departmentName),
    [staffingRule.rules]
  );

  // ── Row Selection → Edit Dialog ──────────────────────────────────

  const handleRowSelected = useCallback((args: RowSelectEventArgs) => {
    if (readOnly || !args.data) return;
    const selected = staffingRule.rules.find(r => r.id === (args.data as DepartmentRuleView).id);
    if (selected) {
      setEditingRule(selected);
      setDialogOpen(true);
    }
  }, [staffingRule.rules, readOnly]);

  // ── Add Button → Add Dialog ──────────────────────────────────────

  const handleAddClick = useCallback(() => {
    setEditingRule(null);
    setDialogOpen(true);
  }, []);

  // ── Dialog Close ─────────────────────────────────────────────────

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingRule(null);
  }, []);

  // ── Save Rule (Add or Update) ────────────────────────────────────

  const handleSaveRule = useCallback(async (saved: DepartmentRuleView) => {
    // Saved without a change, the dialog closes as Cancel does. A write would still move the version
    // token and refuse every other open session's next save for a change nobody made.
    if (editingRule && isUnchanged(editingRule, saved)) return;
    try {
      await onSaveRule(saved);
      setSnackbar({ open: true, message: 'Rule saved successfully.', severity: 'success' });
    } catch (err) {
      // A staleness refusal already has its own warning on the page, and FR-007 says it must not
      // read as a generic failure. Every other rejection still does.
      if (isStaleSaveRefusal(err)) return;
      console.error('Failed to save rule:', err);
      setSnackbar({ open: true, message: 'Failed to save rule.', severity: 'error' });
    }
  }, [editingRule, onSaveRule]);

  return (
    <Box>
      {/* Add Button */}
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <AddRuleButton
            existingDepartments={existingDepartments}
            isSaving={isSaving}
            onClick={handleAddClick}
          />
        </Box>
      )}

      {/* Grid */}
      <RulesGrid
        rules={staffingRule.rules}
        dialogOpen={dialogOpen}
        onRowSelected={handleRowSelected}
      />

      {/* Edit/Add Dialog */}
      <DepartmentRuleDialog
        open={dialogOpen}
        rule={editingRule}
        existingDepartments={existingDepartments}
        onClose={handleCloseDialog}
        onSave={handleSaveRule}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RulesEditorGrid;
